import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getShared } from '../api/images';

export default function SharedImagePage() {
  const { token } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['shared', token],
    queryFn:  () => getShared(token).then((r) => r.data),
    retry:    false,
  });

  const imageSrc = data ? `/uploads/originals/${data.filename}` : null;
  const isPdf    = data?.mime_type === 'application/pdf';
  const isEpub   = data?.mime_type === 'application/epub+zip';

  async function downloadFile(e) {
    e.preventDefault();
    try {
      const res = await fetch(imageSrc, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob   = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = data.original_name || data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch {
      window.open(imageSrc, '_blank', 'noopener');
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Minimal header */}
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="text-brand-400 font-bold text-lg tracking-tight">Snapvault</Link>
          <Link to="/register" className="btn-primary text-sm">Save images free →</Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-8">
        {isLoading && (
          <div className="flex justify-center py-24">
            <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-4 py-24 text-center">
            <p className="text-4xl">🔍</p>
            <p className="text-gray-300 font-medium text-lg">Image not found</p>
            <p className="text-gray-500 text-sm">This share link may have expired or been revoked.</p>
            <Link to="/" className="btn-secondary mt-2">Back to Snapvault</Link>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Meta */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h1 className="text-lg font-semibold text-white truncate max-w-lg">{data.original_name}</h1>
                <p className="text-xs text-gray-500">
                  {data.mime_type} · {(data.size_bytes / 1024).toFixed(1)} KB ·
                  Shared {new Date(data.created_at).toLocaleDateString()}
                </p>
              </div>
              <a
                href={imageSrc}
                download={data.original_name}
                onClick={downloadFile}
                className="btn-primary text-sm shrink-0"
              >
                ↓ Download
              </a>
            </div>

            {/* Preview */}
            <div className="card overflow-hidden flex items-center justify-center p-2 bg-gray-900">
              {isPdf ? (
                <iframe
                  src={imageSrc}
                  title={data.original_name}
                  className="w-full rounded-lg bg-white"
                  style={{ height: '80vh' }}
                />
              ) : isEpub ? (
                <div className="flex flex-col items-center justify-center gap-4 text-center py-16">
                  <svg className="w-20 h-20 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-6 14H8v-2h4v2zm4-4H8v-2h8v2zm0-4H8V6h8v2z"/>
                  </svg>
                  <p className="text-gray-400 text-sm">EPUB files cannot be previewed in the browser. Download to read in an e-reader app.</p>
                </div>
              ) : (
                <img
                  src={imageSrc}
                  alt={data.original_name}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              )}
            </div>

            {/* CTA */}
            <div className="card p-5 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-gray-300 text-sm">
                Want to save and organize images like this?
              </p>
              <Link to="/register" className="btn-primary text-sm shrink-0">
                Get Snapvault free →
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
